/**
 * Main Calendar component wrapper around react-big-calendar
 * Supports drag & drop for rescheduling appointments
 */

import { useCallback, useMemo } from 'react'
import {
	Calendar as BigCalendar,
	type View,
	type SlotInfo,
} from 'react-big-calendar'
import withDragAndDrop, { type EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop'
import { localizer, czechMessages, formatDateCzech, createDayLayoutAlgorithm } from '@/lib/calendar-utils'
import { CALENDAR_CONFIG } from '@/lib/constants'
import {
	CalendarEventComponent,
	createEventStyleGetter,
	getSlotStyle,
} from './CalendarEvent'
import { createDayHeaderComponent } from './CalendarDayHeader'
import { CalendarNotesRow } from './CalendarNotesRow'
import type { CalendarEvent, CalendarView, AppointmentType } from '@/types/calendar'
import type { DayStats } from '@/hooks/useCalendarEvents'

// Import calendar styles
import '@/styles/calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'

// Create drag-and-drop enabled calendar
const DnDCalendar = withDragAndDrop<CalendarEvent>(BigCalendar)

interface CalendarProps {
	events: CalendarEvent[]
	currentDate: Date
	view: CalendarView
	calendarType: AppointmentType
	onNavigate: (date: Date) => void
	onViewChange: (view: CalendarView) => void
	onSelectSlot: (slotInfo: SlotInfo) => void
	onSelectEvent: (event: CalendarEvent) => void
	onEventDrop: (args: EventInteractionArgs<CalendarEvent>) => void
	isLoading?: boolean
	dayStats?: Record<string, DayStats>
	ghostMode?: boolean
}

export function Calendar({
	events,
	currentDate,
	view,
	calendarType,
	onNavigate,
	onViewChange,
	onSelectSlot,
	onSelectEvent,
	onEventDrop,
	isLoading = false,
	dayStats = {},
	ghostMode = false,
}: CalendarProps) {
	// Calendar time boundaries
	const minTime = useMemo(() => {
		const date = new Date()
		date.setHours(CALENDAR_CONFIG.MIN_HOUR, 0, 0, 0)
		return date
	}, [])

	const maxTime = useMemo(() => {
		const date = new Date()
		date.setHours(CALENDAR_CONFIG.MAX_HOUR, 0, 0, 0)
		return date
	}, [])

	// Event handlers
	const handleSelectSlot = useCallback(
		(slotInfo: SlotInfo) => {
			onSelectSlot(slotInfo)
		},
		[onSelectSlot],
	)

	const handleSelectEvent = useCallback(
		(event: CalendarEvent) => {
			onSelectEvent(event)
		},
		[onSelectEvent],
	)

	const handleNavigate = useCallback(
		(date: Date) => {
			onNavigate(date)
		},
		[onNavigate],
	)

	const handleViewChange = useCallback(
		(newView: View) => {
			onViewChange(newView as CalendarView)
		},
		[onViewChange],
	)

	// Custom components
	const components = useMemo(
		() => ({
			event: CalendarEventComponent,
			header: createDayHeaderComponent(dayStats),
		}),
		[dayStats],
	)

	const eventStyleGetter = useMemo(
		() => createEventStyleGetter(calendarType),
		[calendarType],
	)

	const dayLayoutAlgorithm = useMemo(
		() => createDayLayoutAlgorithm(calendarType, ghostMode),
		[calendarType, ghostMode],
	)

	const isMainEvent = useCallback(
		(event: CalendarEvent) => event.resource?.type === calendarType,
		[calendarType],
	)

	// Formats for display with Czech locale
	const formats = useMemo(
		() => ({
			timeGutterFormat: 'H:mm',
			eventTimeRangeFormat: () => null as unknown as string,
			// Czech day format: "Po 20.2." or "Út 21.2."
			dayFormat: (date: Date) => formatDateCzech(date, 'EEEEEE d.M.'),
			weekdayFormat: (date: Date) => formatDateCzech(date, 'EEEEEE'),
			dayHeaderFormat: (date: Date) => formatDateCzech(date, 'EEEE d.M.'),
			dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) => {
				const startStr = formatDateCzech(start, 'd.M.')
				const endStr = formatDateCzech(end, 'd.M.yyyy')
				return `${startStr} – ${endStr}`
			},
		}),
		[],
	)

	return (
		<div className="calendar-with-notes relative h-full">
			{isLoading && (
				<div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-[#d7ad6c] border-t-transparent" />
				</div>
			)}
			{/* Notes row - positioned between header and time grid via CSS */}
			<CalendarNotesRow
				currentDate={currentDate}
				view={view}
				calendarType={calendarType}
			/>
			<DnDCalendar
				localizer={localizer}
				events={events}
				date={currentDate}
				view={view}
				views={['week', 'work_week', 'day']}
				onNavigate={handleNavigate}
				onView={handleViewChange}
				onSelectSlot={handleSelectSlot}
				onSelectEvent={handleSelectEvent}
				onEventDrop={onEventDrop}
				draggableAccessor={isMainEvent}
				resizable={false}
				selectable
				step={CALENDAR_CONFIG.STEP_MINUTES}
				timeslots={CALENDAR_CONFIG.TIMESLOTS_PER_HOUR}
				min={minTime}
				max={maxTime}
				messages={czechMessages}
				components={components}
				formats={formats}
				eventPropGetter={eventStyleGetter}
				slotPropGetter={getSlotStyle}
				dayLayoutAlgorithm={dayLayoutAlgorithm}
				popup
				showMultiDayTimes
				toolbar={false}
			/>
		</div>
	)
}
